import cloneDeep from "lodash.clonedeep";

import { BaseEditor } from "@/libs/map-draw/controllers";
import {
  clearAllHelpers,
  modifyLineRouteItems,
  createDependenciesHelpers,
  removeDependenciesHelpers,
  removeDeletedDepHelpers,
  createDeletedDepHelpers,
  removeStopPointsHelpers,
  createRouteHelper,
  clearAllRouteGeometry,
  setReverseRouteStyling,
  setActiveStopPointsStyling,
  clearReverseRouteStyling,
  clearActiveStopPointsStyling
} from "@/libs/map-draw/helpers";
import { getEditorRequestConfig, debounce, jsonToGeojson } from "@/utils";
import { specialStylingTypes, geomTypesConfig } from "@/libs/map-draw/config";
import { publicTransportLayersConfig } from "../configs";

const pointsName = "sp_points";
const connectorsName = "sp_connectors";

export class EditorController extends BaseEditor {
  constructor(parent) {
    super(parent);
    this.parent = parent;
    this.mapgl = parent.mapgl;
    this.$store = parent.$store;
    this.state = parent.editorState;
  }

  // common helpers
  clearEditorData() {
    super.clearEditorData();

    this.state.dependenciesData = {};
    this.state.deletedDependenciesIds = [];
    removeDependenciesHelpers(this.mapgl);
    removeDeletedDepHelpers(this.mapgl, this.state.modelLayers);
  }

  changeHistoryStep(data) {
    super.changeHistoryStep(data);

    const step = this.state.history[this.state.historyIndex];
    this.state.dependenciesData = cloneDeep(step.dependenciesData);
    this.state.deletedDependenciesIds = cloneDeep(step.deletedDependenciesIds);
    createDependenciesHelpers(this.mapgl, this.state.dependenciesData);
    createDeletedDepHelpers(
      this.mapgl,
      this.state.modelLayers,
      this.state.deletedDependenciesIds
    );
  }

  // handlers
  async saveData() {
    await this.requestForSaving();
    this.clearEditorData();
    this.loadLayerData();
    this.state.modelLayers
      .filter(
        l => l.visible && l.datatype !== "nodes" && l.datatype !== "links"
      )
      .forEach(l => this.handlers[l.id]());
  }

  // model handlers
  toggleEditingModelLayer(layerId) {
    const { id, modelLayers } = this.state;
    if (layerId === id) return;

    const layer = modelLayers.find(l => l.id === layerId);
    const { visible, geom_type, datatype } = layer;

    if (visible) this.toggleVisibleModelLayer(layerId);

    const setNewEditingLayer = () => {
      this.state.id = layerId;
      this.state.geom_type = datatype || geom_type;
      this.state.geometry = geom_type;
      clearAllHelpers(this.mapgl);
      this.setEditorHandler();
      if (id) this.toggleVisibleModelLayer(id);
    };
    const successCb = async () => {
      await this.requestForSaving();
      setNewEditingLayer();
    };
    const errorCb = action => {
      if (action === "cancel") setNewEditingLayer();
    };

    if (this.state.history.length) {
      this.showConfirmModal(successCb, errorCb);
    } else setNewEditingLayer();
  }
  toggleVisibleModelLayer(layerId) {
    const { modelLayers, id } = this.state;
    if (layerId === id) return;
    const layer = modelLayers.find(l => l.id === layerId);
    const { geom_type, datatype } = layer;

    if (layer.loading) return;

    if (layer.visible) {
      // remove handler
      this.mapgl.removeLayer(layerId);
      this.mapgl.removeSource(layerId);
      this.mapgl.off("moveend", this.handlers[layerId]);
      this.removeStopPointsHelpers();
      delete this.handlers[layerId];
      layer.visible = false;
    } else {
      const loadVisibleLayer = async e => {
        if (e && e.noRequest) return;
        const zoom = this.mapgl.getZoom();
        const config = getEditorRequestConfig(this.map, geom_type);

        if (datatype === "stop_points")
          config.only = [
            "id",
            { field: "stop_geom", func: "st_as_geojson" },
            "geom"
          ];

        try {
          this.$store.commit("ADD_PT_LOADING_LAYER", id);
          const url = `objectInfo/${layerId}?config=${JSON.stringify(
            config
          )}&zoom=${zoom}`;
          const { data } = await this.map.$store.dispatch("REQUEST", {
            url
          });
          const features = Object.values(data);
          const geojson = jsonToGeojson(features);

          if (datatype === "stop_points")
            this.setStopPointsHelpers(geojson.features);

          if (!this.mapgl.getLayer(layerId)) {
            this.mapgl.addLayer(
              {
                id: layerId,
                source: {
                  type: "geojson",
                  data: geojson
                },
                ...publicTransportLayersConfig[
                  specialStylingTypes.indexOf(datatype) > -1
                    ? datatype
                    : geom_type
                ]
              },
              datatype === "line_routes"
                ? "gl-draw-point-inactive.cold"
                : "gl-draw-point-inactive.hot"
            );
          } else {
            this.mapgl.getSource(layerId).setData(geojson);
          }

          this.$store.commit("REMOVE_PT_LOADING_LAYER", id);
        } catch (error) {
          console.log(error);
          this.$store.commit("REMOVE_PT_LOADING_LAYER", id);
        }
      };

      this.handlers[layerId] = debounce(loadVisibleLayer, 400);
      this.handlers[layerId]();
      this.mapgl.on("moveend", this.handlers[layerId]);

      layer.visible = true;
    }
  }

  async setEditorHandler() {
    const loadLayerData = async e => {
      if (e && e.noRequest) return;
      let selected = this.draw.getSelectedIds();
      const { id, geom_type, isDrawing } = this.state;
      if (isDrawing) return;
      if (!id) return;

      const zoom = this.mapgl.getZoom();
      const config = getEditorRequestConfig(this.map, geom_type);

      try {
        this.$store.commit("ADD_PT_LOADING_LAYER", id);
        const url = `objectInfo/${id}?config=${JSON.stringify(
          config
        )}&zoom=${zoom}&simplify=off`;
        const { data } = await this.map.$store.dispatch("REQUEST", {
          url
        });
        const geojson = jsonToGeojson(Object.keys(data).map(id => data[id]));
        const editedFeatures = [...this.created, ...this.updated];
        geojson.features = geojson.features
          .filter(f => {
            return (
              !this.deleted.find(
                item => item.properties.id === f.properties.id
              ) &&
              !this.updated.find(item => item.properties.id === f.properties.id)
            );
          })
          .map(f => ({ ...f, id: f.properties.id }));

        if (geom_type === "line_routes") {
          modifyLineRouteItems(geojson.features);
        }

        let features = [...editedFeatures, ...geojson.features];

        this.draw.set({
          type: "FeatureCollection",
          features
        });

        if (this.state.geom_type === "stop_points")
          this.setStopPointsHelpers(features);

        if (this.state.mode === "create") this.changeModeToCreate();
        else {
          if (!this.$store.state.publicTransport.activeCardId) selected = [];
          const { selectedRoute } = this.$store.state.publicTransport;
          if (selectedRoute) {
            const { id } = selectedRoute;
            const feature = this.state.simpleSelectMode.getFeature(id);

            if (!feature) return;

            this.state.activeObject = feature;
            this.$store.commit("SET_PT_STATE_PROP", {
              name: "activeCard",
              value: true
            });
            this.$store.commit("SET_PT_STATE_PROP", {
              name: "activeCardId",
              value: id
            });
            this.$store.commit("SET_ACTIVE_ROUTE_PROPS", {
              reverse: feature.properties.rev,
              reverseRouteId: feature.properties.reverse_route_id
            });
            this.draw.changeMode("simple_select", {
              featureIds: [id]
            });

            const { reverse_route_id, line_route_items } = feature.properties;
            const spIds = line_route_items
              .filter(item => item.stop_point_id)
              .map(item => item.stop_point_id);

            // route arrows
            createRouteHelper(this.mapgl, feature.getCoordinates(), true);
            // reverse route styling
            setReverseRouteStyling(this.mapgl, reverse_route_id);
            // stop points styling
            setActiveStopPointsStyling(this.mapgl, this.$store, spIds);

            this.$store.commit("SET_SELECTED_ROUTE", null);
          } else {
            this.draw.changeMode("simple_select", { featureIds: selected });
          }
        }
        this.$store.commit("REMOVE_PT_LOADING_LAYER", id);
      } catch (error) {
        console.log(error);
        this.$store.commit("REMOVE_PT_LOADING_LAYER", id);
      }
    };

    await this.getLayerObjectFields();
    if (this.loadLayerData) this.mapgl.off("moveend", this.loadLayerData);
    this.loadLayerData = debounce(loadLayerData, 400);
    this.clearEditorData();
    this.loadLayerData();
    this.mapgl.on("moveend", this.loadLayerData);
  }

  // clear data
  clearVisibleLayers() {
    const { id, modelLayers } = this.state;
    const visibleLayers = modelLayers.filter(
      l =>
        l.id !== id &&
        l.visible &&
        l.datatype !== "nodes" &&
        l.datatype !== "links"
    );

    visibleLayers.forEach(l => this.toggleVisibleModelLayer(l.id));
  }
  clearEditedFeatures() {
    this.clearEditorData();
    this.draw.deleteAll();
    removeStopPointsHelpers(this.mapgl);
    if (this.loadLayerData) this.mapgl.off("moveend", this.loadLayerData);
    this.state.id = "";
    this.state.modelId = "";
    this.state.modelLayers = [];
    this.toggleEditorMode("edit");
  }

  // toggle editor
  toggleEditorOff() {
    this.clearVisibleLayers();
    this.clearEditedFeatures();
  }

  toggleEditorOn(mode) {
    const { ids, children, id } = this.$store.state.publicTransport.model;
    const layers = ["stop_points", "line_routes", "nodes", "links"];
    const visible = mode === "stop_points" ? "line_routes" : "stop_points";

    this.state.modelId = id;
    this.state.modelLayers = children
      .filter(l => layers.includes(l.datatype))
      .map(l => ({
        id: l.id,
        datatype: l.datatype,
        geom_type: l.geom_type,
        name: l.name,
        visible: l.datatype === "nodes" || l.datatype === "links",
        loading: false
      }));

    // toggle layers
    this.toggleLayerMode(mode);
    this.toggleVisibleModelLayer(ids[visible]);
  }

  toggleLayerMode(mode) {
    const { ids } = this.$store.state.publicTransport.model;
    this.toggleEditingModelLayer(ids[mode]);
    clearAllRouteGeometry(this.mapgl);
    clearReverseRouteStyling(this.mapgl);
    clearActiveStopPointsStyling(this.mapgl, this.$store);
    removeStopPointsHelpers(this.mapgl);
  }

  // stop points helpers
  getSPPointsData(features) {
    return {
      type: "FeatureCollection",
      features: features
        .filter(f => f.properties.stop_geom)
        .map(f => ({
          ...f,
          geometry: f.properties.stop_geom
        }))
    };
  }
  getSPConnectorsData(features) {
    return {
      type: "FeatureCollection",
      features: features
        .filter(f => f.properties.stop_geom)
        .map(f => ({
          ...f,
          geometry: {
            type: "LineString",
            coordinates: [
              f.geometry.coordinates,
              f.properties.stop_geom.coordinates
            ]
          }
        }))
    };
  }

  setStopPointsHelpers(features) {
    const pointsData = this.getSPPointsData(features);
    const connectorsData = this.getSPConnectorsData(features);
    const pointsSource = this.mapgl.getSource(pointsName);
    const connectorsSource = this.mapgl.getSource(connectorsName);

    if (!pointsSource) {
      this.mapgl.addSource(pointsName, {
        type: "geojson",
        data: pointsData
      });
      this.mapgl.addLayer(
        {
          id: pointsName,
          source: pointsName,
          type: "circle",
          paint: {
            "circle-radius": {
              base: 1.4,
              stops: [[6, 5], [22, 12]]
            },
            "circle-color": "#d63838",
            "circle-pitch-alignment": "map"
          }
        },
        "gl-draw-point-inactive.cold"
      );
    } else {
      pointsSource.setData(pointsData);
    }

    if (!connectorsSource) {
      this.mapgl.addSource(connectorsName, {
        type: "geojson",
        data: connectorsData
      });
      this.mapgl.addLayer(
        {
          id: connectorsName,
          source: connectorsName,
          type: "line",
          layout: {
            "line-join": "round",
            "line-cap": "round"
          },
          paint: {
            "line-color": "#d63838",
            "line-width": 2,
            "line-dasharray": [0.5, 4]
          }
        },
        pointsName
      );
    } else {
      connectorsSource.setData(connectorsData);
    }
  }

  removeStopPointsHelpers() {
    if (this.mapgl.getLayer(pointsName)) this.mapgl.removeLayer(pointsName);
    if (this.mapgl.getLayer(connectorsName))
      this.mapgl.removeLayer(connectorsName);
    if (this.mapgl.getSource(pointsName)) this.mapgl.removeSource(pointsName);
    if (this.mapgl.getSource(connectorsName))
      this.mapgl.removeSource(connectorsName);
  }

  // toggle mode
  toggleEditorMode(newMode) {
    const { mode, geom_type } = this.state;
    if (newMode === mode) return;

    if (newMode === "create") {
      const mode = `draw_${geomTypesConfig[geom_type]}`;
      this.draw.changeMode(mode);
      this.state.mode = "create";
    } else {
      this.draw.changeMode("simple_select");
      this.state.mode = "edit";
    }

    this.$store.commit("SET_PT_STATE_PROP", {
      name: "activeCardId",
      value: null
    });

    removeStopPointsHelpers(this.mapgl);
    clearAllRouteGeometry(this.mapgl);
  }

  // saving callback
  savingCallback() {
    this.$store.commit("SET_PT_UPDATED", true);
  }
}
